import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Tabs, Tab, Paper, Typography, Button, CircularProgress,
  Table, TableBody, TableCell, TableHead, TableRow, IconButton,
  TextField, Checkbox, MenuItem,
} from '@mui/material';
import { Edit as EditIcon, Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { SupplierService } from '../../services/supplier.service';
import { SupplierItemService } from '../../services/supplierItem.service';
import { ItemService } from '../../services/item.service';
import { Supplier } from '../../interfaces/procurement/supplier.interface';
import { SupplierItem, SupplierItemCreateDTO } from '../../interfaces/procurement/supplierItem.interface';
import { Item } from '../../interfaces/item.interface';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';

// SupplierController's write endpoints and every SupplierItemController endpoint are StoreAdmin /
// InstitutionAdmin only. An Accountant may read the supplier but gets a 403 on the catalogue and on edit.
const WRITE_ROLES = ['StoreAdmin', 'InstitutionAdmin'];
const currentRoles = (): string[] => {
  const raw = AuthService.getCurrentUser()?.roles;
  return Array.isArray(raw) ? raw : raw ? [raw] : [];
};

export const SupplierDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const canWrite = currentRoles().some(r => WRITE_ROLES.includes(r));
  const toast = useToast();
  const [tab, setTab] = useState(0);
  const [supplier, setSupplier] = useState<Supplier | null>(null);
  const [catalogue, setCatalogue] = useState<SupplierItem[]>([]);
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);
  const [newRow, setNewRow] = useState<{ itemId: string; unitPrice: number; preferred: boolean }>({
    itemId: '', unitPrice: 0, preferred: false,
  });

  useEffect(() => { loadAll(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadAll = async () => {
    try {
      setLoading(true);
      // The catalogue (and the item names it needs) is only fetched for roles SupplierItemController
      // admits; fetching it for an Accountant would 403 and fail the whole page.
      const [s, c, itemsRes] = await Promise.all([
        SupplierService.getById(id!),
        canWrite ? SupplierItemService.listBySupplier(id!) : Promise.resolve([] as SupplierItem[]),
        canWrite ? ItemService.getItemsByInstitution(0, 200) : Promise.resolve(null),
      ]);
      setSupplier(s);
      setCatalogue(c);
      setItems(itemsRes?.content ?? []);
    } catch (e) {
      toast.showApiError(e, 'Failed to load supplier detail');
    } finally {
      setLoading(false);
    }
  };

  const addCatalogueRow = async () => {
    if (!newRow.itemId || newRow.unitPrice <= 0) {
      toast.showApiError(null, 'Pick an item and enter a positive price');
      return;
    }
    try {
      const dto: SupplierItemCreateDTO = {
        supplierId: id!,
        itemId: newRow.itemId,
        unitPrice: newRow.unitPrice,
        preferred: newRow.preferred,
        institutionId: AuthService.getSelectedInstitution()!!,
      };
      await SupplierItemService.create(dto);
      setNewRow({ itemId: '', unitPrice: 0, preferred: false });
      const c = await SupplierItemService.listBySupplier(id!);
      setCatalogue(c);
    } catch (e) {
      toast.showApiError(e, 'Failed to add catalogue row');
    }
  };

  const deleteCatalogueRow = async (rowId: string) => {
    if (!window.confirm('Delete this catalogue row?')) return;
    try {
      await SupplierItemService.delete(rowId);
      setCatalogue(c => c.filter(r => r.id !== rowId));
    } catch (e) {
      toast.showApiError(e, 'Failed to delete catalogue row');
    }
  };

  if (loading || !supplier) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5">{supplier.name}</Typography>
        {canWrite && (
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/procurement/suppliers/${id}/edit`)}
          >
            Edit
          </Button>
        )}
      </Box>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        {/* Explicit values: hiding Catalogue must not shift the other tabs' indices. */}
        <Tab label="Overview" value={0} />
        {canWrite && <Tab label="Catalogue" value={1} />}
        <Tab label="Purchase History" value={2} />
      </Tabs>

      {tab === 0 && (
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 1, alignItems: 'center' }}>
            <Typography color="text.secondary">Contact</Typography>
            <Typography>{supplier.contactPerson ?? '—'}</Typography>
            <Typography color="text.secondary">Email</Typography>
            <Typography>{supplier.email ?? '—'}</Typography>
            <Typography color="text.secondary">Phone</Typography>
            <Typography>{supplier.phone ?? '—'}</Typography>
            <Typography color="text.secondary">Address</Typography>
            <Typography>{supplier.address ?? '—'}</Typography>
            <Typography color="text.secondary">Tax Reg No</Typography>
            <Typography>{supplier.taxRegistrationNo ?? '—'}</Typography>
          </Box>
        </Paper>
      )}

      {tab === 1 && (
        <Paper sx={{ p: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Item</TableCell>
                <TableCell>Unit Price</TableCell>
                <TableCell>Preferred</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {catalogue.map(c => (
                <TableRow key={c.id}>
                  <TableCell>{items.find(i => i.id === c.itemId)?.itemName ?? c.itemId}</TableCell>
                  <TableCell>{c.unitPrice.toFixed(4)}</TableCell>
                  <TableCell>{c.preferred ? '✓' : ''}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => deleteCatalogueRow(c.id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell>
                  <TextField
                    select
                    size="small"
                    fullWidth
                    value={newRow.itemId}
                    onChange={e => setNewRow({ ...newRow, itemId: e.target.value })}
                  >
                    {items.map(i => (
                      <MenuItem key={i.id} value={i.id}>{i.itemName}</MenuItem>
                    ))}
                  </TextField>
                </TableCell>
                <TableCell>
                  <TextField
                    type="number"
                    size="small"
                    value={newRow.unitPrice}
                    onChange={e => setNewRow({ ...newRow, unitPrice: Number(e.target.value) })}
                  />
                </TableCell>
                <TableCell>
                  <Checkbox
                    checked={newRow.preferred}
                    onChange={e => setNewRow({ ...newRow, preferred: e.target.checked })}
                  />
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" color="primary" onClick={addCatalogueRow}>
                    <AddIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Paper>
      )}

      {tab === 2 && (
        <Paper sx={{ p: 2 }}>
          {/* Purchase history: v1 = simple "see Purchase Orders list filtered by this supplier" link */}
          <Button
            variant="text"
            onClick={() => navigate(`/procurement/purchase-orders?supplierId=${id}`)}
          >
            View Purchase Orders for this Supplier →
          </Button>
        </Paper>
      )}
    </Box>
  );
};
